"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.EntityPopup = void 0;
var react_1 = __importStar(require("react"));
var office_ui_fabric_react_1 = require("office-ui-fabric-react");
require("./styles/entity-popup.scss");
/**
 * Centered Fluent Modal shell shared by all entity popups. Provides:
 *  - colored header band with kind-specific accent,
 *  - close header control,
 *  - scrollable body region with a fixed maximum height,
 *  - footer with secondary/primary action groups.
 */
exports.EntityPopup = function (_a) {
    var isOpen = _a.isOpen, onDismiss = _a.onDismiss, kind = _a.kind, modifier = _a.modifier, header = _a.header, children = _a.children, primaryActions = _a.primaryActions, secondaryActions = _a.secondaryActions;
    var leftPrimaryActions = (primaryActions || []).filter(function (action) { return action.key === 'toggle-active'; });
    var rightPrimaryActions = (primaryActions || []).filter(function (action) { return action.key !== 'toggle-active'; });
    var rootClass = react_1.useMemo(function () {
        var parts = ['ws-entity-popup', "ws-entity-popup--" + kind];
        if (modifier) {
            parts.push("ws-entity-popup--" + modifier);
        }
        return parts.join(' ');
    }, [kind, modifier]);
    // Fluent's Modal mounts a Layer/overlay that briefly toggles the page
    // scrollbar gutter and, on dismiss, focus restoration can scroll the
    // page back near the top. Snapshot the scroll position when the modal
    // opens and restore it after it closes so the reader stays put.
    var savedScrollYRef = react_1.useRef(null);
    var wasOpenRef = react_1.useRef(false);
    // ---- DEBUG LOGGING (scroll behaviour around popup open/close) ----
    // Toggle by setting `window.__WS_DEBUG_POPUP_SCROLL = false` in console.
    var debugLog = function (label, extra) {
        if (process.env.NODE_ENV === 'production') {
            return;
        }
        if (typeof window !== 'undefined' && window.__WS_DEBUG_POPUP_SCROLL === false) {
            return;
        }
        var docEl = document.documentElement;
        var body = document.body;
        // eslint-disable-next-line no-console
        console.log("[EntityPopup:" + kind + "] " + label, __assign({ isOpen: isOpen, scrollY: window.scrollY, docScrollTop: docEl.scrollTop, bodyScrollTop: body.scrollTop, innerHeight: window.innerHeight, docScrollHeight: docEl.scrollHeight, bodyScrollHeight: body.scrollHeight, docClientHeight: docEl.clientHeight, hasVerticalScrollbar: docEl.scrollHeight > docEl.clientHeight, bodyOverflow: getComputedStyle(body).overflow, docOverflow: getComputedStyle(docEl).overflow, activeElement: document.activeElement
                ? document.activeElement.tagName +
                    (document.activeElement.className
                        ? '.' +
                            document.activeElement.className
                                .toString()
                                .split(' ')
                                .slice(0, 2)
                                .join('.')
                        : '')
                : null, saved: savedScrollYRef.current, ts: performance.now().toFixed(1) }, (extra || {})));
    };
    // Track every scroll while the popup lifecycle is active so we can see
    // exactly when (and by whom) the page jumps.
    react_1.useEffect(function () {
        var frame = null;
        var lastY = window.scrollY;
        var onScroll = function () {
            if (frame !== null) {
                return;
            }
            frame = requestAnimationFrame(function () {
                frame = null;
                var y = window.scrollY;
                var dy = y - lastY;
                lastY = y;
                debugLog('window scroll', { y: y, dy: dy });
            });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return function () {
            if (frame !== null) {
                cancelAnimationFrame(frame);
            }
            window.removeEventListener('scroll', onScroll);
        };
        // We want this listener active for the full life of the popup component
        // (mount → unmount), independent of `isOpen`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    react_1.useEffect(function () {
        if (isOpen && !wasOpenRef.current) {
            var y = window.scrollY || document.documentElement.scrollTop || 0;
            savedScrollYRef.current = y;
            wasOpenRef.current = true;
            // Mark the body so our CSS can keep it scrollable while Fluent's
            // Modal tries to lock it (`body { overflow: hidden }` inline). The
            // `.ws-entity-popup-open` rule overrides that with `!important`,
            // which prevents both the open-time jump (scrollTop clamped to 0
            // when scrollHeight collapses) and the on-close lost scroll.
            document.body.classList.add('ws-entity-popup-open');
            debugLog('OPEN — snapshot taken', { savedJustNow: y });
            requestAnimationFrame(function () {
                debugLog('OPEN +1 frame');
                requestAnimationFrame(function () { return debugLog('OPEN +2 frames'); });
            });
            return;
        }
        if (!isOpen && wasOpenRef.current) {
            wasOpenRef.current = false;
            savedScrollYRef.current = null;
            document.body.classList.remove('ws-entity-popup-open');
            debugLog('CLOSE — leaving scroll where user left it');
            // Intentionally NOT restoring window.scrollTo here. The user may
            // have scrolled while the popup was open and expects to stay at
            // their current position after closing.
        }
    }, [isOpen]);
    return (react_1.default.createElement(office_ui_fabric_react_1.Modal, { isOpen: isOpen, onDismiss: onDismiss, isBlocking: false, containerClassName: 'ws-entity-popup-container', overlay: { className: 'ws-entity-popup-overlay' }, 
        // Fluent's FocusTrapZone otherwise auto-focuses the first focusable
        // element inside the modal (the Close IconButton). Because the modal
        // portal is appended at the bottom of `<body>`, the browser then
        // smooth-scrolls the whole page to bring that focused button into
        // view — which manifests as the page sliding up to scrollY=0 right
        // after the popup opens. Disabling first-focus stops that.
        focusTrapZoneProps: {
            disableFirstFocus: true,
            forceFocusInsideTrap: false,
        } },
        react_1.default.createElement("div", { className: rootClass, role: "dialog", "aria-modal": "true" },
            react_1.default.createElement("div", { className: "ws-entity-popup-header" },
                react_1.default.createElement("div", { className: "ws-entity-popup-header-content" },
                    " ",
                    header),
                react_1.default.createElement("div", { className: "ws-entity-popup-header-actions" },
                    react_1.default.createElement(office_ui_fabric_react_1.IconButton, { className: "ws-entity-popup-header-button", iconProps: { iconName: 'Cancel' }, title: "Close", ariaLabel: "Close", onClick: onDismiss }))),
            react_1.default.createElement("div", { className: "ws-entity-popup-body" }, children),
            (primaryActions && primaryActions.length > 0) ||
                (secondaryActions && secondaryActions.length > 0) ? (react_1.default.createElement("div", { className: "ws-entity-popup-footer" },
                react_1.default.createElement("div", { className: "ws-entity-popup-footer-group" },
                    (secondaryActions || []).map(function (action) { return (react_1.default.createElement("button", { key: action.key, type: "button", className: "ws-entity-popup-footer-button is-secondary", disabled: action.disabled, onClick: action.onClick },
                        action.iconName ? (react_1.default.createElement("i", { className: 'ms-Icon ms-Icon--' +
                                action.iconName +
                                ' ws-entity-popup-footer-button-icon', "aria-hidden": "true" })) : null,
                        react_1.default.createElement("span", null, action.label))); }),
                    leftPrimaryActions.map(function (action) { return (react_1.default.createElement("button", { key: action.key, type: "button", className: 'ws-entity-popup-footer-button' +
                            (action.primary !== false ? ' is-primary' : ' is-secondary'), disabled: action.disabled, onClick: action.onClick },
                        react_1.default.createElement("span", null, action.label),
                        action.iconName ? (react_1.default.createElement("i", { className: 'ms-Icon ms-Icon--' +
                                action.iconName +
                                ' ws-entity-popup-footer-button-icon', "aria-hidden": "true" })) : null)); })),
                react_1.default.createElement("div", { className: "ws-entity-popup-footer-group" }, rightPrimaryActions.map(function (action) { return (react_1.default.createElement("button", { key: action.key, type: "button", className: 'ws-entity-popup-footer-button' +
                        (action.primary !== false ? ' is-primary' : ' is-secondary'), disabled: action.disabled, onClick: action.onClick },
                    react_1.default.createElement("span", null, action.label),
                    action.iconName ? (react_1.default.createElement("i", { className: 'ms-Icon ms-Icon--' +
                            action.iconName +
                            ' ws-entity-popup-footer-button-icon', "aria-hidden": "true" })) : null)); })))) : null)));
};
